import React, { useEffect, useMemo, useState } from 'react';
import { Card, Spin, Alert, Select, Typography } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import type { RootState } from '@/store';
import {
  getDashboardMetric24,
  type DashboardMetric24Period,
  type DashboardMetric24GapActivity,
} from '@/services/dashboardService';

const { Text } = Typography;

type WaterfallRow = {
  key: 'INITIAL' | 'OPENED' | 'CLOSED' | 'FINAL';
  label: string;
  base: number;
  delta: number;
  total: number;
  isTotal: boolean;
};

const COLORS = {
  initial: '#6B7280',
  opened: '#EF4444',
  closed: '#00A37A',
  final: '#2563EB',
};

const PERIOD_OPTIONS: { value: DashboardMetric24Period; label: string }[] = [
  { value: 'yeartodate', label: 'Year to Date' },
  { value: 'quarterone', label: 'Quarter 1' },
  { value: 'quartertwo', label: 'Quarter 2' },
  { value: 'quarterthree', label: 'Quarter 3' },
  { value: 'quarterfour', label: 'Quarter 4' },
];

export type EX1QuarterlyYTDGapActivityWaterfallChartProps = {
  height?: number;
  title?: string;
};

const Metric24Tooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const row: WaterfallRow | undefined = payload?.[0]?.payload;
  if (!row) return null;

  const displayValue = row.isTotal ? row.total : Math.abs(row.delta);

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: 8,
        padding: 10,
        minWidth: 200,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{row.label}</div>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}
      >
        <span>{row.isTotal ? 'Count' : 'GAPs'}</span>
        <span style={{ fontWeight: 700 }}>{displayValue}</span>
      </div>
    </div>
  );
};

export const EX1QuarterlyYTDGapActivityWaterfallChart: React.FC<
  EX1QuarterlyYTDGapActivityWaterfallChartProps
> = ({
  height = 360,
  title = 'GAP Activity',
}) => {
  const navigate = useNavigate();
  const { selectedReport } = useSelector(
    (state: RootState) => state.annualReport,
  );
  const annualReportId = selectedReport?.annualReportId;

  const [period, setPeriod] = useState<DashboardMetric24Period>('yeartodate');
  const [raw, setRaw] = useState<DashboardMetric24GapActivity | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      if (!annualReportId) {
        setRaw(null);
        setErrorMessage(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      try {
        const data = await getDashboardMetric24(annualReportId, period);
        if (!mounted) return;
        setRaw(data);
      } catch (err: any) {
        if (!mounted) return;
        setRaw(null);
        setErrorMessage(
          err?.message || err?.Message || 'Failed to load Metric 24.',
        );
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    };

    run();
    return () => {
      mounted = false;
    };
  }, [annualReportId, period]);

  const rows: WaterfallRow[] = useMemo(() => {
    if (!raw) return [];

    const initial = raw.initialGapCount;
    const opened = raw.openedGapsCount;
    const closed = raw.closedGapsCount;
    const final_ = raw.finalGapCount;

    return [
      {
        key: 'INITIAL',
        label: 'Initial GAPs',
        base: 0,
        delta: 0,
        total: initial,
        isTotal: true,
      },
      {
        key: 'OPENED',
        label: 'GAPs Opened',
        base: initial,
        delta: opened,
        total: 0,
        isTotal: false,
      },
      {
        key: 'CLOSED',
        label: 'GAPs Closed',
        base: Math.max(initial + opened - closed, 0),
        delta: closed,
        total: 0,
        isTotal: false,
      },
      {
        key: 'FINAL',
        label: 'Final GAPs',
        base: 0,
        delta: 0,
        total: final_,
        isTotal: true,
      },
    ];
  }, [raw]);

  const isEmpty = !loading && !errorMessage && rows.length === 0;

  const periodToQuarter: Record<DashboardMetric24Period, string | null> = {
    yeartodate: null,
    quarterone: '1',
    quartertwo: '2',
    quarterthree: '3',
    quarterfour: '4',
  };

  const multiPeriodQuarters: Record<DashboardMetric24Period, unknown> = {
    yeartodate: ['4', '3', '2', '1'],
    quarterone: ['1', '0'],
    quartertwo: ['2', '1', '0'],
    quarterthree: ['3', '2', '1', '0'],
    quarterfour: ['4', '3', '2', '1', '0'],
  };

  const handleBarClick = (row: WaterfallRow) => {
    const statusMap: Record<WaterfallRow['key'], string[]> = {
      INITIAL: ['Open', 'Archived'],
      OPENED: ['Open', 'Archived'],
      CLOSED: ['Open', 'Archived'],
      FINAL: ['Open'],
    };

    const q = periodToQuarter[period];
    const qList = multiPeriodQuarters[period];
    const state: Record<string, unknown> = { status: statusMap[row.key] };

    if (row.key === 'OPENED') {
      if (qList && period == 'yeartodate')
        state.gapDateQuarter = qList;
      else if (q)
        state.gapDateQuarter = [q];
    } else if (row.key === 'CLOSED') {
      if (qList && period == 'yeartodate')
        state.gapClosureDateQuarter = qList;
      else if (q)
        state.gapClosureDateQuarter = [q];
    }
    else if (qList && row.key === 'INITIAL') {
      if (period == 'yeartodate') {
        state.gapDateQuarter = ['0'];
        state.gapClosureDateQuarter = ['0']
      }
      else {
        state.gapDateQuarter = (qList as string[]).slice(1);
        state.gapClosureDateQuarter = (qList as string[]).slice(1);
      }
    }
    else if (q && row.key === 'FINAL') {
      state.gapDateQuarter = qList;
      state.gapClosureDateQuarter = qList;
    }

    navigate('/gaps', { state });
  };

  const barColorForRow = (r: WaterfallRow) => {
    if (r.key === 'INITIAL') return COLORS.initial;
    if (r.key === 'OPENED') return COLORS.opened;
    if (r.key === 'CLOSED') return COLORS.closed;
    return COLORS.final;
  };

  const cardTitle = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span>{title}</span>
      <Select
        value={period}
        onChange={setPeriod}
        style={{ width: 160 }}
        size='small'
        options={PERIOD_OPTIONS}
      />
    </div>
  );

  return (
    <Card title={cardTitle}>
      <div style={{ height }}>
        {loading ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spin />
          </div>
        ) : errorMessage ? (
          <Alert
            type='error'
            message='Dashboard Metric 24 Error'
            description={errorMessage}
            showIcon
          />
        ) : isEmpty ? (
          <Alert
            type='info'
            message='No data'
            description='No GAP activity data returned for this period.'
            showIcon
          />
        ) : (
          <ResponsiveContainer width='100%' height='95%'>
            <BarChart
              data={rows}
              margin={{ top: 30, right: 24, bottom: 24, left: 16 }}
            >
              <XAxis dataKey='label' />
              <YAxis
                label={{
                  value: '# of GAPs',
                  angle: -90,
                  position: 'insideLeft',
                  offset: -6,
                }}
              />
              <Tooltip content={<Metric24Tooltip />} />

              <Bar dataKey='base' stackId='wf' fill='rgba(0,0,0,0)' />

              <Bar
                dataKey={(r: any) => (r.isTotal ? r.total : r.delta)}
                stackId='wf'
                isAnimationActive={false}
                onClick={(_data: any, _index: number, e: any) => {
                  const row = _data?.payload as WaterfallRow | undefined;
                  if (row) handleBarClick(row);
                  e?.stopPropagation?.();
                }}
                style={{ cursor: 'pointer' }}
              >
                <LabelList
                  style={{ fill: 'black' }}
                  dataKey={(r: any) =>
                    r.isTotal ? r.total : (r.key === 'CLOSED' ? -r.delta : r.delta)
                  }
                  position='top'
                />
                {rows.map((r, idx) => (
                  <Cell key={`cell-${idx}`} fill={barColorForRow(r)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
        <div style={{ marginTop: 6, textAlign: 'center' }}>
          <Text type='secondary'>
            Initial + Opened − Closed = Final
          </Text>
        </div>
      </div>
    </Card>
  );
};

export default EX1QuarterlyYTDGapActivityWaterfallChart;
